import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import apiClient from '../../services/apiService';

const ACTIVE_JOB_KEY = 'vendorImportActiveJob';

const ExcelImportDropzone = ({ onImportComplete, onError, cleanImport = false, className = '' }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importStage, setImportStage] = useState('idle'); // idle, uploading, parsing, importing, complete
  const [importResult, setImportResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const pollingRef = useRef(null);
  const hasCheckedActiveJob = useRef(false);

  // Save active job to localStorage
  const saveActiveJob = useCallback((jobData) => {
    try {
      localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify({
        jobId: jobData.jobId,
        fileName: jobData.fileName,
        fileSize: jobData.fileSize,
        startedAt: jobData.startedAt || new Date().toISOString(),
      }));
    } catch (e) {
      console.warn('Failed to save active job to localStorage:', e);
    }
  }, []);

  // Clear active job from localStorage
  const clearActiveJob = useCallback(() => {
    try {
      localStorage.removeItem(ACTIVE_JOB_KEY);
    } catch (e) {
      console.warn('Failed to clear active job from localStorage:', e);
    }
  }, []);

  // Get active job from localStorage
  const getActiveJob = useCallback(() => {
    try {
      const stored = localStorage.getItem(ACTIVE_JOB_KEY);
      if (stored) {
        const job = JSON.parse(stored);
        // Check if job is not too old (max 1 hour)
        const startedAt = new Date(job.startedAt);
        const now = new Date();
        const hourInMs = 60 * 60 * 1000;
        if (now - startedAt < hourInMs) {
          return job;
        }
        // Job is too old, clear it
        clearActiveJob();
      }
    } catch (e) {
      console.warn('Failed to get active job from localStorage:', e);
    }
    return null;
  }, [clearActiveJob]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '';
    if (seconds < 60) return `~${seconds}s remaining`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `~${minutes}m ${secs}s remaining`;
  };

  const getStageMessage = () => {
    if (jobProgress) {
      const { progress, status } = jobProgress;
      if (status === 'importing') {
        return `Importing vendors... ${progress.processedRows} of ${progress.totalRows}`;
      }
      if (status === 'parsing') {
        return 'Parsing Excel file...';
      }
      if (status === 'completed') {
        return 'Import complete!';
      }
      if (status === 'failed') {
        return 'Import failed';
      }
      if (status === 'pending') {
        return 'Import starting...';
      }
    }
    
    switch (importStage) {
      case 'uploading':
        return `Uploading ${fileName}...`;
      case 'parsing':
        return 'Parsing Excel file...';
      case 'importing':
        return jobId && !jobProgress ? 'Reconnecting to import job...' : 'Starting import...';
      case 'complete':
        return 'Import complete!';
      default:
        return 'Drag & drop Excel file here';
    }
  };

  // Poll for job status
  const pollJobStatus = useCallback(async (jobIdToPoll) => {
    try {
      const response = await apiClient.get(`/vendors/bulk/import-status/${jobIdToPoll}`);
      const data = response.data.data;
      
      setJobProgress(data);
      
      // Update file name from job data if available
      if (data.fileName && !fileName) {
        setFileName(data.fileName);
      }
      
      if (data.status === 'completed') {
        // Job completed successfully
        setImportResult({
          summary: data.summary,
          imported_vendor_codes: data.imported_vendor_codes,
          warnings: data.warnings,
          errors: data.errors,
          failed_rows: data.failed_rows,
        });
        setImportStage('complete');
        setIsUploading(false);
        clearActiveJob(); // Clear from localStorage
        onImportComplete?.({
          summary: data.summary,
          imported_vendor_codes: data.imported_vendor_codes,
          warnings: data.warnings,
          errors: data.errors,
          failed_rows: data.failed_rows,
        });
        
        // Stop polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } else if (data.status === 'failed') {
        // Job failed
        setIsUploading(false);
        setImportStage('idle');
        clearActiveJob(); // Clear from localStorage
        onError?.(data.errorMessage || 'Import job failed');
        
        // Stop polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
      // else keep polling for 'pending', 'parsing', 'importing' statuses
      
    } catch (error) {
      console.error('Error polling job status:', error);
      // If job not found (404) or invalid (422), clear it
      if (error.response?.status === 404 || error.response?.status === 422) {
        console.warn('Import job no longer exists or is invalid, clearing active job');
        clearActiveJob();
        setIsUploading(false);
        setImportStage('idle');
        setJobId(null);
        setJobProgress(null);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        // Optionally show a message to the user
        onError?.('Import job was interrupted or no longer exists. Please start a new import.');
      }
      // Don't stop polling on other temporary errors (network issues, etc.)
    }
  }, [onImportComplete, onError, clearActiveJob, fileName]);

  // Check for active job on mount and resume polling if found
  useEffect(() => {
    if (hasCheckedActiveJob.current) return;
    hasCheckedActiveJob.current = true;

    const activeJob = getActiveJob();
    if (activeJob) {
      console.log('Resuming active import job:', activeJob);
      setJobId(activeJob.jobId);
      setFileName(activeJob.fileName || '');
      setFileSize(activeJob.fileSize || 0);
      setIsUploading(true);
      setImportStage('importing');
      
      // Start polling immediately
      pollJobStatus(activeJob.jobId);
      
      // Continue polling every 2 seconds
      pollingRef.current = setInterval(() => {
        pollJobStatus(activeJob.jobId);
      }, 2000);
    }
  }, [getActiveJob, pollJobStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const allowedExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      onError?.('Please upload a valid Excel (.xlsx, .xls) or CSV file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setImportResult(null);
    setFileName(file.name);
    setFileSize(file.size);
    setImportStage('uploading');
    setJobId(null);
    setJobProgress(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('cleanImport', cleanImport.toString());

      // Upload file - this returns immediately with a job ID
      const response = await apiClient.post('/vendors/bulk/import-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 1 minute for upload only
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          
          if (percentCompleted === 100) {
            setImportStage('parsing');
          }
        },
      });

      const result = response.data;
      console.log('Import job started:', result);
      
      if (result.jobId) {
        // Background job mode - start polling
        setJobId(result.jobId);
        setImportStage('importing');
        
        // Save to localStorage for persistence across page navigation
        saveActiveJob({
          jobId: result.jobId,
          fileName: file.name,
          fileSize: file.size,
        });
        
        // Start polling every 2 seconds
        pollingRef.current = setInterval(() => {
          pollJobStatus(result.jobId);
        }, 2000);
        
        // Also poll immediately
        pollJobStatus(result.jobId);
      } else {
        // Synchronous mode (fallback) - result is already complete
        console.log('Import completed synchronously:', result);
        setImportResult(result);
        setImportStage('complete');
        setUploadProgress(100);
        setIsUploading(false);
        onImportComplete?.(result);
      }

    } catch (error) {
      console.error('Import error:', error);
      let errorMessage = 'Failed to import vendors';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timed out. Please check your connection and try again.';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      onError?.(errorMessage);
      setImportStage('idle');
      setIsUploading(false);
    }
  }, [onImportComplete, onError, pollJobStatus]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    multiple: false,
    disabled: isUploading,
  });

  const downloadTemplate = async () => {
    try {
      const response = await apiClient.get('/vendors/bulk/template', {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'vendor_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to download template';
      onError?.(errorMessage);
    }
  };

  const downloadErrorReport = async () => {
    if (!jobId) {
      onError?.('No job ID available for error report');
      return;
    }

    try {
      const response = await apiClient.get(`/vendors/bulk/import-errors/${jobId}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `vendor_import_errors_${jobId}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to download error report';
      onError?.(errorMessage);
    }
  };

  const resetImport = () => {
    setImportResult(null);
    setImportStage('idle');
    setUploadProgress(0);
    setFileName('');
    setFileSize(0);
    setJobId(null);
    setJobProgress(null);
    clearActiveJob();
    
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Calculate progress for display
  const progressPercent = jobProgress?.progress?.percentComplete || 
    (importStage === 'uploading' ? uploadProgress : 
     importStage === 'complete' ? 100 : 0);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
          ${isDragActive && !isDragReject 
            ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
            : isDragReject 
            ? 'border-red-500 bg-red-50' 
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
          ${isUploading ? 'pointer-events-none' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center space-y-4">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto">
            {isUploading ? (
              <div className="relative w-16 h-16">
                <svg className="animate-spin w-16 h-16 text-blue-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600">
                    {progressPercent}%
                  </span>
                </div>
              </div>
            ) : importStage === 'complete' ? (
              <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg 
                className={`w-16 h-16 ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                />
              </svg>
            )}
          </div>
          
          {/* Text */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {getStageMessage()}
            </h3>
            {!isUploading && importStage !== 'complete' && (
              <>
                <p className="text-sm text-gray-500">
                  or click to select file
                </p>
                <p className="text-xs text-gray-400">
                  Supports .xlsx, .xls, and .csv files (up to 10MB)
                </p>
                {cleanImport && (
                  <div className="mt-3 px-4 py-2 bg-orange-100 border border-orange-300 rounded-lg">
                    <p className="text-xs text-orange-800 font-semibold">
                      Clean Import Mode Active - All vendor data will be deleted before import
                    </p>
                  </div>
                )}
              </>
            )}
            {isUploading && fileName && (
              <p className="text-sm text-gray-500">
                {fileName} ({formatFileSize(fileSize)})
              </p>
            )}
          </div>

          {/* Progress Bar */}
          {isUploading && (
            <div className="w-full max-w-md space-y-2">
              <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${
                    importStage === 'complete' ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>
                  {importStage === 'uploading' && `Uploading: ${uploadProgress}%`}
                  {importStage === 'parsing' && 'Parsing data...'}
                  {importStage === 'importing' && jobProgress && (
                    <>
                      Batch {jobProgress.progress?.currentBatch || 0} of {jobProgress.progress?.totalBatches || 0}
                      {' • '}
                      {jobProgress.summary?.successful_imports || 0} imported
                    </>
                  )}
                  {importStage === 'importing' && !jobProgress && 'Starting import...'}
                </span>
                <span>
                  {jobProgress?.progress?.estimatedSecondsRemaining && 
                    formatTime(jobProgress.progress.estimatedSecondsRemaining)
                  }
                </span>
              </div>
              
              {/* Live stats during import */}
              {importStage === 'importing' && jobProgress?.summary && (
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="text-green-600">
                    ✓ {jobProgress.summary.successful_imports} imported
                  </span>
                  <span className="text-red-600">
                    ✗ {jobProgress.summary.failed_imports} failed
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Template Download & Tips */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
        <button
          onClick={downloadTemplate}
          className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
          disabled={isUploading}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download Excel template
        </button>
        <p className="text-xs text-gray-400">
          💡 Large files (5000+ rows) are processed in background with live progress
        </p>
      </div>

      {/* Import Results */}
      {importResult && (
        <div className="mt-6 space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900 text-lg">Import Results</h4>
              <button
                onClick={resetImport}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Import Another
              </button>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-600">
                  {importResult.summary?.successful_imports || 0}
                </div>
                <div className="text-sm text-green-700 font-medium">Successful</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-red-600">
                  {importResult.summary?.failed_imports || 0}
                </div>
                <div className="text-sm text-red-700 font-medium">Failed</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {importResult.summary?.warnings_count || 0}
                </div>
                <div className="text-sm text-yellow-700 font-medium">Warnings</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {importResult.summary?.total_processed || 0}
                </div>
                <div className="text-sm text-blue-700 font-medium">Total Processed</div>
              </div>
            </div>

            {/* Batch Processing Info */}
            {importResult.summary?.total_batches > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <span>Processed in {importResult.summary.batches_processed} batches</span>
                </div>
                <span className="text-gray-500">
                  (100 vendors per batch)
                </span>
              </div>
            )}

            {/* Success Rate & Message */}
            {importResult.summary?.total_processed > 0 && (
              <div className={`border rounded-lg p-4 mb-4 ${
                importResult.summary.failed_imports === 0 
                  ? 'bg-green-50 border-green-200' 
                  : importResult.summary.successful_imports === 0 
                  ? 'bg-red-50 border-red-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {importResult.summary.failed_imports === 0 ? (
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : importResult.summary.successful_imports === 0 ? (
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    <span className={`font-medium ${
                      importResult.summary.failed_imports === 0 
                        ? 'text-green-800' 
                        : importResult.summary.successful_imports === 0 
                        ? 'text-red-800'
                        : 'text-yellow-800'
                    }`}>
                      {importResult.summary.failed_imports === 0 
                        ? `All ${importResult.summary.successful_imports} vendors imported successfully!`
                        : importResult.summary.successful_imports === 0
                        ? 'Import failed - no vendors were imported'
                        : `${importResult.summary.successful_imports} of ${importResult.summary.total_processed} vendors imported`
                      }
                    </span>
                  </div>
                  <div className="text-sm font-medium px-2 py-1 rounded bg-white/50">
                    {Math.round((importResult.summary.successful_imports / importResult.summary.total_processed) * 100)}% success rate
                  </div>
                </div>
                {/* Success rate bar */}
                <div className="mt-3 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div 
                    className={`h-2 rounded-full transition-all ${
                      importResult.summary.failed_imports === 0 
                        ? 'bg-green-500' 
                        : importResult.summary.successful_imports === 0 
                        ? 'bg-red-500'
                        : 'bg-gradient-to-r from-green-500 to-yellow-500'
                    }`}
                    style={{ 
                      width: `${Math.round((importResult.summary.successful_imports / importResult.summary.total_processed) * 100)}%` 
                    }}
                  />
                </div>
              </div>
            )}

            {/* Imported Vendor Codes */}
            {importResult.imported_vendor_codes?.length > 0 && importResult.imported_vendor_codes.length <= 50 && (
              <div className="mb-4">
                <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                  Imported Vendor Codes:
                </h5>
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  {importResult.imported_vendor_codes.slice(0, 50).map((code, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md font-mono"
                    >
                      {code}
                    </span>
                  ))}
                  {importResult.imported_vendor_codes.length > 50 && (
                    <span className="px-2 py-1 text-gray-500 text-xs">
                      +{importResult.imported_vendor_codes.length - 50} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Warnings */}
            {importResult.warnings?.length > 0 && (
              <div className="mb-4">
                <h5 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Warnings ({importResult.warnings.length}):
                </h5>
                <div className="max-h-32 overflow-y-auto bg-yellow-50 rounded-lg p-3">
                  {importResult.warnings.slice(0, 20).map((warning, index) => (
                    <div key={index} className="text-sm text-yellow-700 py-1">
                      {warning}
                    </div>
                  ))}
                  {importResult.warnings.length > 20 && (
                    <div className="text-sm text-yellow-600 font-medium mt-2">
                      +{importResult.warnings.length - 20} more warnings
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors */}
            {importResult.errors?.length > 0 && (
              <div className="mb-4">
                <h5 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Errors ({importResult.errors.length}):
                </h5>
                <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3">
                  {importResult.errors.slice(0, 30).map((error, index) => (
                    <div key={index} className="text-sm text-red-700 py-1 border-b border-red-100 last:border-0">
                      {error}
                    </div>
                  ))}
                  {importResult.errors.length > 30 && (
                    <div className="text-sm text-red-600 font-medium mt-2">
                      +{importResult.errors.length - 30} more errors
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Failed Rows */}
            {importResult.failed_rows?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-red-800 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Failed Rows ({importResult.failed_rows.length}):
                  </h5>
                  <button
                    onClick={downloadErrorReport}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download Error Report (Excel)
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto bg-red-50 rounded-lg p-3">
                  {importResult.failed_rows.slice(0, 20).map((row, index) => (
                    <div key={index} className="text-sm text-red-700 py-2 border-b border-red-100 last:border-0">
                      <div className="font-medium">Row {row.row}: {row.company_name}</div>
                      {row.errors?.map((error, errorIndex) => (
                        <div key={errorIndex} className="text-xs text-red-600 ml-4 mt-1">
                          • {error}
                        </div>
                      ))}
                    </div>
                  ))}
                  {importResult.failed_rows.length > 20 && (
                    <div className="text-sm text-red-600 font-medium mt-2 pt-2 border-t border-red-200">
                      +{importResult.failed_rows.length - 20} more failed rows
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelImportDropzone;
